import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  FileText,
  Loader,
  PlayCircle,
  RotateCcw,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import {
  DOCUMENTATION_STATUSES,
  DOCUMENTATION_STATUS_COLORS,
  DOCUMENTATION_STATUS_LABELS,
  DocumentationListResponse,
  DocumentationStats,
  DocumentationStatus,
  PopulatedDocumentation,
} from "../types/documentation";
import api from "../utils/api";
import { formatDate } from "utils/documentation/formatDate";
import { getStatusColor } from "utils/documentation/getStatusColor/getStatusColor";
import { DeleteDocumentationModal } from "./DocumentationModals";
import VideoUploadWizard from "./VideoUploadWizard";
import { FilterPanel } from "components/common/FilterPanel";

interface DocumentationManagementProps {
  projectId?: string;
  sectionId?: string;
}

const DOCUMENTATION_STATUS_OPTIONS = DOCUMENTATION_STATUSES.map((status) => ({
  value: status,
  label: DOCUMENTATION_STATUS_LABELS[status],
}));

const DocumentationManagement: React.FC<DocumentationManagementProps> = ({
  projectId: propProjectId,
  sectionId: propSectionId,
}) => {
  const { t } = useTranslation();
  const { id: paramProjectId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const projectId = propProjectId || paramProjectId;

  const [documentation, setDocumentation] = useState<PopulatedDocumentation[]>(
    []
  );

  const [stats, setStats] = useState<DocumentationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Filters and pagination
  const [statusFilter, setStatusFilter] = useState<DocumentationStatus | "all">(
    "all"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const { user } = useAuth();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showVideoUpload, setShowVideoUpload] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<PopulatedDocumentation | null>(
    null
  );
  const [statusEditId, setStatusEditId] = useState<string | null>(null);

  const fetchDocumentation = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);

    try {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      const response: DocumentationListResponse =
        await api.getDocumentationBySection(propSectionId, params);

      setDocumentation(response.documentation || []);
      setTotalPages(response.pagination?.total || 0);

      // Fetch stats for the project
      const statsResponse = await api.getDocumentationStats(projectId);
      setStats(statsResponse);
    } catch (err: any) {
      setError(err.message || "Failed to fetch documentation");
    } finally {
      setLoading(false);
    }
  }, [projectId, propSectionId, currentPage, itemsPerPage, statusFilter]);

  useEffect(() => {
    fetchDocumentation();
  }, [fetchDocumentation]);

  const handleDelete = async (docId: string) => {
    setActionLoading(docId);
    try {
      await api.deleteDocumentation(docId);
      await fetchDocumentation(); // Refresh the list
      setShowDeleteModal(false);
      setSelectedDoc(null);
    } catch (err: any) {
      setError(err.message || "Failed to delete documentation");
    } finally {
      setActionLoading(null);
    }
  };

  const handleView = (doc: PopulatedDocumentation) => {
    navigate(
      `/project/${projectId}/sections/${propSectionId}/documentation/${doc._id}`
    );
  };

  const handleStatusChange = useCallback(
    async (doc: PopulatedDocumentation, newStatus: DocumentationStatus) => {
      setActionLoading(`status-${doc._id}`);
      try {
        await api.updateDocumentationStatus(doc._id, newStatus);
        await fetchDocumentation();
      } catch (err: any) {
        setError(err.message || "Failed to update documentation status");
      } finally {
        setActionLoading(null);
        setStatusEditId(null);
      }
    },
    [fetchDocumentation]
  );

  const filteredDocumentation = documentation.filter((doc) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        String(doc.sectionId).toLowerCase().includes(searchLower) ||
        doc.status.toLowerCase().includes(searchLower) ||
        String(doc._id).toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const getStatusIndicator = (status: DocumentationStatus) => {
    const getIcon = () => {
      switch (status) {
        case "created":
          return <Clock className="h-4 w-4" />;
        case "in_process":
          return <RotateCcw className="h-4 w-4 animate-spin" />;
        case "in_validation":
          return <AlertCircle className="h-4 w-4" />;
        case "done":
          return <CheckCircle className="h-4 w-4" />;
        default:
          return <XCircle className="h-4 w-4" />;
      }
    };

    const getProgressBar = () => {
      const progressMap = {
        created: 25,
        in_process: 50,
        in_validation: 75,
        done: 100,
      };
      const progress = progressMap[status] || 0;

      return (
        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${
              status === "done"
                ? "bg-green-500"
                : status === "in_validation"
                ? "bg-yellow-500"
                : status === "in_process"
                ? "bg-blue-500"
                : "bg-gray-400"
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      );
    };

    return (
      <div
        className={`px-3 py-2 rounded-lg border ${getStatusColor({ status })}`}
      >
        <div className="flex items-center space-x-2">
          {getIcon()}
          <span className="text-xs font-medium">
            {DOCUMENTATION_STATUS_LABELS[status]}
          </span>
        </div>
        {getProgressBar()}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="animate-spin h-8 w-8 mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600">{t("documentation.loading")}</p>
        </div>
      </div>
    );
  }

  const handleUploadComplete = () => {
    setShowVideoUpload(false);
    fetchDocumentation(); // Refresh the documentation list
  };

  return (
    <>
      <div className="space-y-3">
        {/* Header */}
        <div className="flex justify-end">
          <button
            onClick={() => setShowVideoUpload(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Upload className="h-4 w-4 mr-2" />
            {t("documentation.upload_video")}
          </button>
        </div>
        {/* Stats Cards - Compact Version */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-3">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <FileText className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="ml-3 w-0 flex-1">
                    <dl>
                      <dt className="text-xs font-medium text-gray-500 truncate">
                        {t("documentation.total")}
                      </dt>
                      <dd className="text-base font-semibold text-gray-900">
                        {stats.total}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            {DOCUMENTATION_STATUSES.map((status) => (
              <div
                key={status}
                className="bg-white overflow-hidden shadow rounded-lg"
              >
                <div className="p-3">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div
                        className={`h-2 w-2 rounded-full ${
                          DOCUMENTATION_STATUS_COLORS[status].split(" ")[0]
                        }`}
                      />
                    </div>
                    <div className="ml-2 w-0 flex-1">
                      <dl>
                        <dt className="text-xs font-medium text-gray-500 truncate">
                          {DOCUMENTATION_STATUS_LABELS[status]}
                        </dt>
                        <dd className="text-base font-semibold text-gray-900">
                          {stats.byStatus[status] || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <FilterPanel
            className="p-3 border-b border-gray-200 "
            searchTerm={searchTerm}
            onSearchChange={(term) => {
              setSearchTerm(term);
              setCurrentPage(1); // Reset to first page on filter change
            }}
            statusFilter={statusFilter}
            onStatusChange={(status) => {
              setStatusFilter(status);
              setCurrentPage(1); // Reset to first page on filter change
            }}
            statuses={DOCUMENTATION_STATUS_OPTIONS}
            onRefresh={fetchDocumentation}
            searchPlaceholder={t("documentation.search_placeholder")}
            allStatusesLabel={t("documentation.all_statuses")}
          />
          {/* Documentation List */}
          <div className="overflow-hidden">
            {filteredDocumentation.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">
                  {searchTerm || statusFilter !== "all"
                    ? t("documentation.no_results")
                    : t("documentation.no_documentation")}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("documentation.created")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("documentation.status")}
                      </th>
                      {/* Only show frames/objects columns if there are completed docs */}
                      {filteredDocumentation.some(
                        (doc) => doc.status === "done"
                      ) && (
                        <>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t("documentation.frames")}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t("documentation.objects")}
                          </th>
                        </>
                      )}
                      {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('documentation.duration')}
                    </th> */}
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("documentation.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredDocumentation.map((doc) => {
                      const isViewable =
                        doc.status === "done" || user?.isGlobalAdmin;

                      return (
                        <tr
                          key={doc._id}
                          onClick={() => isViewable && handleView(doc)}
                          className={`
                            ${doc.status !== "done" ? "bg-gray-25" : ""}
                            ${
                              isViewable
                                ? "cursor-pointer hover:bg-blue-50 hover:shadow-sm"
                                : "cursor-not-allowed"
                            }
                            transition-all duration-150
                          `}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                              {formatDate({ date: doc.createdAt })}
                            </div>
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap"
                            onClick={(e) => {
                              // Prevent row click when interacting with status
                              if (user?.isGlobalAdmin) {
                                e.stopPropagation();
                              }
                            }}
                          >
                            {user?.isGlobalAdmin ? (
                              statusEditId === doc._id ? (
                                <select
                                  autoFocus
                                  value={doc.status}
                                  onChange={(e) =>
                                    handleStatusChange(
                                      doc,
                                      e.target.value as DocumentationStatus
                                    )
                                  }
                                  onBlur={() =>
                                    actionLoading === `status-${doc._id}`
                                      ? null
                                      : setStatusEditId(null)
                                  }
                                  disabled={
                                    actionLoading === `status-${doc._id}`
                                  }
                                  className="px-2 py-1 text-xs border rounded-md bg-white"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {DOCUMENTATION_STATUSES.map((status) => (
                                    <option key={status} value={status}>
                                      {DOCUMENTATION_STATUS_LABELS[status]}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setStatusEditId(doc._id);
                                  }}
                                  className="hover:opacity-90"
                                  title={t("documentation.change_status")}
                                >
                                  {getStatusIndicator(doc.status)}
                                </button>
                              )
                            ) : (
                              getStatusIndicator(doc.status)
                            )}
                          </td>
                          {/* Only show frames/objects columns if there are completed docs */}
                          {filteredDocumentation.some(
                            (d) => d.status === "done"
                          ) && (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {doc.status === "done" ? (
                                  <div className="flex items-center">
                                    <PlayCircle className="h-4 w-4 text-gray-400 mr-1" />
                                    {doc.frameCount}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-xs">
                                    {t("documentation.processing_status")}
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {doc.status === "done" ? (
                                  doc.totalObjectCount
                                ) : (
                                  <span className="text-gray-400 text-xs">
                                    -
                                  </span>
                                )}
                              </td>
                            </>
                          )}
                          {/* <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Clock className="h-4 w-4 text-gray-400 mr-1" />
                          {formatDuration(doc.processingDuration)}
                        </div>
                      </td> */}
                          <td
                            className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
                            onClick={(e) => e.stopPropagation()} // Prevent row click when clicking action buttons
                          >
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleView(doc)}
                                disabled={!isViewable}
                                className={`
                                  inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                                  ${
                                    isViewable
                                      ? "bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                  }
                                `}
                                title={
                                  isViewable
                                    ? t("documentation.view")
                                    : t("documentation.not_ready_for_viewing")
                                }
                              >
                                <Eye className="h-4 w-4 mr-1.5" />
                                {t("documentation.view")}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDoc(doc);
                                  setShowDeleteModal(true);
                                }}
                                className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors"
                                title={t("documentation.delete")}
                                disabled={actionLoading === doc._id}
                              >
                                {actionLoading === doc._id ? (
                                  <Loader className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-3 py-2 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <p className="text-xs text-gray-700">
                    {t("documentation.page")} {currentPage}{" "}
                    {t("documentation.of")} {totalPages}
                  </p>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {t("documentation.error")}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => setError(null)}
                    className="bg-red-50 text-red-800 rounded-md px-2 py-1 text-sm hover:bg-red-100"
                  >
                    {t("documentation.dismiss")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Delete Modal */}
        <DeleteDocumentationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedDoc(null);
          }}
          onConfirm={() => selectedDoc && handleDelete(selectedDoc._id)}
          documentation={selectedDoc}
          loading={actionLoading === selectedDoc?._id}
        />
      </div>
      {showVideoUpload && (
        <VideoUploadWizard
          onClose={() => setShowVideoUpload(false)}
          projectId={projectId!}
          sectionId={propSectionId!}
          onUploadComplete={handleUploadComplete}
        />
      )}
    </>
  );
};

export default DocumentationManagement;
